import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { type GetShiftResponse } from "@src/appV2/Shifts/Shift/api/useGetShift";
import { DEFAULT_TIMEZONE } from "@src/appV2/Shifts/Shift/constants";
import { parseISO } from "date-fns";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  createShiftVerificationRequestFormSchema,
  type CreateShiftVerificationRequestFormValues,
} from "../types";
import { useSendShiftVerificationRequest } from "../useSendShiftVerificationRequest";
import { getShiftVerificationDefaultClockTimes } from "../utils";
import { CreateShiftVerificationRequestDetailsForm } from "./CreateShiftVerificationRequestDetailsForm";
import { CreateShiftVerificationRequestSignatureForm } from "./CreateShiftVerificationRequestSignatureForm";
import { CreateShiftVerificationRequestTimesForm } from "./CreateShiftVerificationRequestTimesForm";

interface ShiftVerificationRequestFormProps {
  shiftId: string;
  shift: Pick<GetShiftResponse["response"], "facility" | "start" | "geofence">;
  onSuccess: () => void;
}

export function ShiftVerificationRequestForm(props: ShiftVerificationRequestFormProps) {
  const { shiftId, shift, onSuccess } = props;

  const workplaceTimezone = shift?.facility?.tmz ?? DEFAULT_TIMEZONE;
  const isCaliforniaTimesheetEnabled =
    shift.facility?.featureSettings?.californiaTimesheet ?? false;

  const defaultClockTimes = useMemo(() => getShiftVerificationDefaultClockTimes(shift), [shift]);

  const shiftVerificationForm = useForm<CreateShiftVerificationRequestFormValues>({
    resolver: zodResolver(createShiftVerificationRequestFormSchema),
    defaultValues: {
      isCdph530Required: isCaliforniaTimesheetEnabled,
      shiftTimes: defaultClockTimes,
    },
    mode: "all",
    reValidateMode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = shiftVerificationForm;

  const { showErrorToast } = useToast();
  const { mutateAsync: createShiftVerificationRequest } = useSendShiftVerificationRequest();

  const onSubmit = async (formData: CreateShiftVerificationRequestFormValues) => {
    try {
      await createShiftVerificationRequest({
        shiftId,
        data: {
          clockInTime: formData.shiftTimes.clockIn.toISOString(),
          breakStartTime: formData.shiftTimes.breakStart?.toISOString(),
          breakEndTime: formData.shiftTimes.breakEnd?.toISOString(),
          clockOutTime: formData.shiftTimes.clockOut.toISOString(),

          cdph530: formData.isCdph530Required
            ? {
                nursingServiceAssignment: formData.nursingServicesAssignment ?? "",
                stationWingUnitFloor: formData.stationUnitWingFloor ?? "",
              }
            : undefined,
          reasonForVerification: formData.reason,
          signatureImageBase64Url: formData.signature,
          workerAcceptedAcknowledgement: formData.acknowledgement,
        },
      });

      onSuccess();
    } catch (error: unknown) {
      showErrorToast("Failed to submit shift verification request. Please try again.");
      logError(APP_V2_APP_EVENTS.SHIFT_VERIFICATION_REQUEST_CREATION_FAILED, {
        error,
        metadata: {
          shiftId,
          formData,
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormProvider<CreateShiftVerificationRequestFormValues> {...shiftVerificationForm}>
        <Stack spacing={6}>
          <Stack spacing={5} px={5} width="100%">
            <CreateShiftVerificationRequestTimesForm
              workplaceTimezone={workplaceTimezone}
              shiftStartTime={parseISO(shift.start)}
            />
          </Stack>

          <Stack
            spacing={6}
            px={5}
            py={6}
            sx={{ backgroundColor: (theme) => theme.palette.background.primary }}
          >
            <CreateShiftVerificationRequestDetailsForm
              isCalifornia={isCaliforniaTimesheetEnabled}
            />
            <CreateShiftVerificationRequestSignatureForm
              workplaceTimezone={workplaceTimezone}
              shiftGeofence={shift?.geofence}
            />
            <Box>
              <Button
                fullWidth
                type="submit"
                size="large"
                variant="contained"
                disabled={isSubmitting || !isValid}
                // The gray background is matching the disabled background color of the button
                // so this is a temporary workaround to keep the button visible even when disabled
                sx={{
                  "&.Mui-disabled": {
                    backgroundColor: (theme) => theme.palette.background.secondary,
                  },
                }}
                onSubmit={handleSubmit(onSubmit)}
              >
                Request workplace verification
              </Button>
            </Box>
          </Stack>
        </Stack>
      </FormProvider>
    </form>
  );
}
